/**
 * IngestionHealthPanel
 *
 * Shows per-source ingestion stats: jobs run, success rate, artifacts archived,
 * bytes stored, and last-run status. Reads directly from Supabase (client-side,
 * RLS-enforced). Displayed on the /analytics page for all roles.
 */
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Archive,
  CheckCircle,
  XCircle,
  Clock,
  HardDrive,
  Activity,
} from 'lucide-react';

interface SourceHealth {
  id: string;
  name: string;
  last_status: string | null;
  last_run_at: string | null;
  jobsRun: number;
  succeeded: number;
  artifacts: number;
  bytesStored: number;
}

function fmtBytes(n: number) {
  if (n === 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtDate(s: string | null) {
  if (!s) return 'Never';
  return new Date(s).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status || status === 'idle')
    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="w-3 h-3" /> Idle
      </Badge>
    );
  if (status === 'succeeded')
    return (
      <Badge className="bg-green-100 text-green-700 gap-1 border-green-200">
        <CheckCircle className="w-3 h-3" /> OK
      </Badge>
    );
  if (status === 'running')
    return (
      <Badge className="bg-blue-100 text-blue-700 gap-1 border-blue-200">
        <Activity className="w-3 h-3 animate-pulse" /> Running
      </Badge>
    );
  if (status === 'failed')
    return (
      <Badge className="bg-red-100 text-red-700 gap-1 border-red-200">
        <XCircle className="w-3 h-3" /> Failed
      </Badge>
    );
  return <Badge variant="outline">{status}</Badge>;
}

export const IngestionHealthPanel: React.FC = () => {
  const [rows, setRows] = useState<SourceHealth[]>([]);
  const [loading, setLoading] = useState(true);

  // Summary totals
  const totJobs = rows.reduce((s, r) => s + r.jobsRun, 0);
  const totArtifacts = rows.reduce((s, r) => s + r.artifacts, 0);
  const totBytes = rows.reduce((s, r) => s + r.bytesStored, 0);
  const totSucceeded = rows.reduce((s, r) => s + r.succeeded, 0);
  const successRate =
    totJobs > 0 ? ((totSucceeded / totJobs) * 100).toFixed(2) : null;

  useEffect(() => {
    async function load() {
      setLoading(true);

      // Sources
      const { data: sources } = await supabase
        .from('sources')
        .select('id, name, last_status, last_run_at')
        .order('name');

      if (!sources?.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Scrape job counts per source
      const { data: jobs } = await supabase
        .from('scrape_jobs')
        .select('source_id, status');

      // Artifact counts + bytes per source
      const { data: arts } = await supabase
        .from('artifacts')
        .select('source_id, byte_size');

      const health: SourceHealth[] = sources.map((src) => {
        const srcJobs = (jobs ?? []).filter((j) => j.source_id === src.id);
        const srcArts = (arts ?? []).filter((a) => a.source_id === src.id);
        return {
          id: src.id,
          name: src.name,
          last_status: src.last_status,
          last_run_at: src.last_run_at,
          jobsRun: srcJobs.length,
          succeeded: srcJobs.filter((j) => j.status === 'succeeded').length,
          artifacts: srcArts.length,
          bytesStored: srcArts.reduce((s, a) => s + (a.byte_size ?? 0), 0),
        };
      });

      setRows(health);
      setLoading(false);
    }
    void load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Activity className="w-4 h-4 animate-spin" />
        Loading ingestion health…
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Card className="bg-gray-50 border-dashed">
        <CardContent className="py-6 text-center text-gray-500">
          <Archive className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">
            No sources registered yet. Add one on the{' '}
            <a href="/sources" className="text-blue-600 underline">
              Sources
            </a>{' '}
            page.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Sources', value: rows.length, icon: Activity },
          {
            label: 'Jobs run',
            value: totJobs,
            icon: CheckCircle,
          },
          {
            label: 'Success rate',
            value: successRate !== null ? `${successRate}%` : '—',
            icon: CheckCircle,
          },
          {
            label: 'Artifacts',
            value: totArtifacts.toLocaleString(),
            icon: Archive,
          },
          {
            label: 'Stored',
            value: fmtBytes(totBytes),
            icon: HardDrive,
          },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardContent className="py-4 flex items-center gap-3">
              <Icon className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-lg font-semibold text-gray-900">{value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Per-source table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-gray-500">
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-right px-4 py-2 font-medium">Jobs</th>
                <th className="text-right px-4 py-2 font-medium">Artifacts</th>
                <th className="text-right px-4 py-2 font-medium">Stored</th>
                <th className="text-left px-4 py-2 font-medium">Last run</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b last:border-0 hover:bg-gray-50"
                >
                  <td className="px-4 py-2 font-medium text-gray-900">
                    {r.name}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {r.jobsRun}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {r.artifacts.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">
                    {fmtBytes(r.bytesStored)}
                  </td>
                  <td className="px-4 py-2 text-gray-500">
                    {fmtDate(r.last_run_at)}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={r.last_status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
